import React, { useState, useRef, useEffect } from 'react';
import { Bot, Send, Loader2, ShieldCheck, CheckCircle2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Checkbox } from './ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { saasApi } from '../api/client';

export const ReceptionistChat = ({ firm }) => {
  const [open, setOpen] = useState(false);
  const [convId, setConvId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [ready, setReady] = useState(false);
  const [summary, setSummary] = useState('');
  const [consent, setConsent] = useState(false);
  const [sent, setSent] = useState(false);
  const bottomRef = useRef(null);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages, ready, sent]);

  const start = async () => {
    if (convId) return;
    setBusy(true);
    try {
      const data = await saasApi.convStart(firm.slug);
      setConvId(data.id);
      setMessages([{ role: 'assistant', text: data.reply }]);
    } catch (err) {
      setMessages([{ role: 'assistant', text: err.response?.data?.detail || 'Atendimento indisponível no momento.' }]);
    } finally { setBusy(false); }
  };

  const send = async (e) => {
    e?.preventDefault();
    const text = input.trim();
    if (!text || busy || !convId) return;
    setInput('');
    setMessages(m => [...m, { role: 'user', text }]);
    setBusy(true);
    try {
      const data = await saasApi.convMessage(convId, text);
      setMessages(m => [...m, { role: 'assistant', text: data.reply }]);
      if (data.ready) { setReady(true); setSummary(data.summary); }
    } catch {
      setMessages(m => [...m, { role: 'assistant', text: 'Desculpe, tive um problema. Pode repetir?' }]);
    } finally { setBusy(false); }
  };

  const authorize = async () => {
    setBusy(true);
    try {
      await saasApi.convConsent(convId, true);
      setSent(true);
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (o) start(); }}>
      <DialogTrigger asChild>
        <Button className="bg-[#153e44] hover:bg-[#0c3036] text-white h-11" data-testid="receptionist-open-btn">
          <Bot className="w-4 h-4 mr-2 text-[#075a6d]" /> Atendimento digital
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg p-0 gap-0 overflow-hidden">
        <DialogHeader className="bg-[#f7fafb] border-b border-[#cbd8dc] text-[#122f36] p-4">
          <DialogTitle className="flex items-center gap-2 text-base">
            <div className="w-9 h-9 rounded-full bg-[#edf4f5] border border-[#cbd8dc] flex items-center justify-center"><Bot className="w-5 h-5 text-[#153e44]" /></div>
            <div>
              <div>Recepcionista digital</div>
              <div className="text-[11px] font-normal text-[#425a62]">{firm.name} • Triagem administrativa, sem orientação jurídica</div>
            </div>
          </DialogTitle>
        </DialogHeader>

        <div className="h-80 overflow-y-auto p-4 space-y-3 bg-[#f7fafb]" data-testid="receptionist-messages">
          {messages.map((m, i) => (
            <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[85%] rounded-[10px] px-3.5 py-2 text-sm ${m.role === 'user' ? 'bg-[#153e44] text-white rounded-br-sm' : 'bg-white border border-[#cbd8dc] text-[#354c54] rounded-bl-sm'}`}>
                {m.text}
              </div>
            </div>
          ))}
          {busy && <div className="flex justify-start"><div className="bg-white border border-[#cbd8dc] rounded-[10px] px-3.5 py-2"><Loader2 className="w-4 h-4 animate-spin text-[#075a6d]" /></div></div>}

          {ready && !sent && (
            <div className="bg-white border-2 border-[#cbd8dc] rounded-[7px] p-4 space-y-3" data-testid="receptionist-consent-panel">
              <div className="text-xs font-semibold text-[#153e44] uppercase tracking-wider flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5 text-[#075a6d]" /> Resumo do atendimento</div>
              <p className="text-sm text-[#354c54] whitespace-pre-line">{summary}</p>
              <label className="flex items-start gap-2.5 cursor-pointer">
                <Checkbox data-testid="receptionist-consent-checkbox" checked={consent} onCheckedChange={v => setConsent(!!v)} className="mt-0.5" />
                <span className="text-xs text-[#425a62]">Autorizo o envio destas informações exclusivamente ao escritório <strong>{firm.name}</strong> para fins de contato (LGPD, consentimento v1.0-2026-06).</span>
              </label>
              <div className="flex gap-2">
                <Button size="sm" disabled={!consent || busy} onClick={authorize} className="flex-1 bg-[#153e44] hover:bg-[#0c3036] text-white font-semibold" data-testid="receptionist-authorize-btn">Autorizar e enviar</Button>
                <Button size="sm" variant="outline" onClick={() => setOpen(false)} data-testid="receptionist-decline-btn">Não enviar</Button>
              </div>
            </div>
          )}

          {sent && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-[7px] p-4 text-center" data-testid="receptionist-sent-panel">
              <CheckCircle2 className="w-8 h-8 text-emerald-600 mx-auto mb-2" />
              <p className="text-sm font-semibold text-emerald-800">Enviado com seu consentimento!</p>
              <p className="text-xs text-emerald-700 mt-1">O escritório recebeu seu atendimento e entrará em contato pelo canal escolhido.</p>
            </div>
          )}
          <div ref={bottomRef} />
        </div>

        {!sent && !ready && (
          <form onSubmit={send} className="flex gap-2 p-3 border-t border-[#cbd8dc] bg-white">
            <Input data-testid="receptionist-input" value={input} onChange={e => setInput(e.target.value)} placeholder="Digite sua mensagem..." disabled={busy || !convId} maxLength={1000} />
            <Button type="submit" size="icon" disabled={busy || !input.trim()} className="bg-[#153e44] hover:bg-[#0c3036] shrink-0" data-testid="receptionist-send-btn"><Send className="w-4 h-4" /></Button>
          </form>
        )}
        <div className="px-4 py-2 bg-[#f2f6f7] text-[10px] text-[#52676e] text-center">Este atendimento inicial não cria relação advogado-cliente e não constitui orientação jurídica.</div>
      </DialogContent>
    </Dialog>
  );
};
